import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Form } from "react-bootstrap";
import { UilDraggabledots } from "@iconscout/react-unicons";

import { TreeNodeRoute } from "./types/treeNode";
import { DropTarget } from "./dropLogic";

interface TreeNodeProps {
    node: TreeNodeRoute;
    depth: number;
    collapsed: Set<string>;
    activeId: string | null;
    dropInfo: DropTarget | null;

    onToggleIncluded: (id: string) => void;
    onToggleCollapse: (id: string) => void;

    dragEnabled: boolean;
    checkboxEnabled: boolean;

    rowStyle?: (node: TreeNodeRoute) => React.CSSProperties | undefined;

    renderNodeExtras?: (
        node: TreeNodeRoute,
        updateNode: (id: string, updater: (n: TreeNodeRoute) => TreeNodeRoute) => void
    ) => React.ReactNode;

    updateNode?: (
        id: string,
        updater: (n: TreeNodeRoute) => TreeNodeRoute
    ) => void;
}

export function TreeNode({
    node,
    depth,
    collapsed,
    activeId,
    dropInfo,
    onToggleIncluded,
    onToggleCollapse,
    dragEnabled,
    checkboxEnabled,
    rowStyle,
    renderNodeExtras,
    updateNode
}: TreeNodeProps) {

    const sortable = useSortable({ id: node.id });

    const setNodeRef = dragEnabled ? sortable.setNodeRef : undefined;
    const transform = dragEnabled ? sortable.transform : null;
    const isDragging = dragEnabled ? sortable.isDragging : false;
    const attributes = dragEnabled ? sortable.attributes : {};
    const listeners = dragEnabled ? sortable.listeners : {};

    const isCollapsed = collapsed.has(node.id);
    const isIncluded = node.included ?? false;

    const isDropAbove =
        dropInfo?.node.id === node.id && dropInfo.position === "above";
    const isDropBelow =
        dropInfo?.node.id === node.id && dropInfo.position === "below";
    const isDropInside =
        dropInfo?.node.id === node.id && dropInfo.position === "inside";

    return (
        <div style={{ position: "relative" }}>
            {isDropAbove && (
                <div
                    style={{
                        position: "absolute",
                        top: -3,
                        left: depth * 24,
                        right: 4,
                        height: 3,
                        borderRadius: 2,
                        background: "var(--bs-primary)"
                    }}
                />
            )}

            <div
                ref={setNodeRef}
                className={`tree-node tree-${node.type}`}
                style={{
                    marginLeft: depth * 24,
                    marginBottom: 6,
                    padding: "2px 12px",
                    borderRadius: 8,
                    border: isDragging
                        ? "1px solid var(--bs-primary)"
                        : "1px solid var(--bs-border-color)",

                    boxShadow: isDragging
                        ? "0 4px 10px rgba(0,0,0,0.25)"
                        : "var(--bs-box-shadow-sm)",

                    transform: CSS.Transform.toString(transform),
                    transition: "all 160ms ease",

                    opacity: isIncluded ? (isDragging ? 0.55 : 1) : 0.45,
                    filter: isIncluded ? "none" : "grayscale(0.3)",

                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    userSelect: "none",
                    ...(rowStyle ? rowStyle(node) : {})
                }}
            >
                {dragEnabled && (
                    <span
                        {...attributes}
                        {...listeners}
                        className="tree-drag-handle"
                        style={{
                            padding: 4,
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "grab",
                            flexShrink: 0,
                            transition: "background 150ms ease"
                        }}
                    >
                        <UilDraggabledots
                            size="18"
                            className="tree-node-dragicon"
                        />
                    </span>
                )}

                {node.children.length > 0 && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleCollapse(node.id);
                        }}
                        className="tree-node-collapse"
                        style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: 12,
                            width: 22
                        }}
                    >
                        {isCollapsed ? "▶" : "▼"}
                    </button>
                )}

                {checkboxEnabled && (
                    <Form.Check
                        type="checkbox"
                        checked={isIncluded}
                        onChange={() => onToggleIncluded(node.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ margin: 0 }}
                    />
                )}

                <span
                    className="tree-node-title"
                    style={{
                        flex: 1,
                        fontWeight: 600,
                        fontSize: 12,
                        color: "var(--phoenix-tertiary-color)"
                    }}
                >
                    {node.title}
                </span>

                {/* ==========================================
                    NOVA ÁREA PARA COMPONENTES EXTRAS
                =========================================== */}
                {renderNodeExtras && updateNode && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {renderNodeExtras(node, updateNode)}
                    </div>
                )}
            </div>

            {isDropInside && (
                <div
                    style={{
                        background: "rgba(38,132,255,0.18)",
                        height: 10,
                        marginLeft: depth * 24 + 28,
                        borderRadius: 4,
                        marginBottom: 4
                    }}
                />
            )}

            {!isCollapsed &&
                node.children.map((child) => (
                    <TreeNode
                        key={child.id}
                        node={child}
                        depth={depth + 1}
                        collapsed={collapsed}
                        activeId={activeId}
                        dropInfo={dropInfo}
                        onToggleIncluded={onToggleIncluded}
                        onToggleCollapse={onToggleCollapse}
                        dragEnabled={dragEnabled}
                        checkboxEnabled={checkboxEnabled}
                        rowStyle={rowStyle}
                        renderNodeExtras={renderNodeExtras}
                        updateNode={updateNode}
                    />
                ))}

            {isDropBelow && (
                <div
                    style={{
                        height: 3,
                        background: "#2684ff",
                        borderRadius: 2,
                        marginLeft: depth * 24,
                        marginTop: 2,
                        marginRight: 4
                    }}
                />
            )}
        </div>
    );
}